import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { MagnifyingGlassIcon, DocumentIcon, FolderIcon, LinkIcon } from '@heroicons/react/24/outline';
import { documentRelationshipService } from '../../../services/documentRelationshipService';
import { DocumentRelationshipType, RelationshipSearchResult } from '../../../types/documents';
import { documentRelationshipTypeService } from '../../../services/documentRelationshipTypeService';
import { useToast } from '../../UI/ToastProvider';

export interface RelatedDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  onRelationshipCreated: () => void;
}

const nodeTypeLabel: Record<string, string> = {
  folder: 'Pasta',
  edoc: 'Documento',
  file: 'Arquivo',
};

export const RelatedDocumentsModal: React.FC<RelatedDocumentsModalProps> = ({
  isOpen,
  onClose,
  documentId,
  onRelationshipCreated,
}) => {
  const { addToast } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<RelationshipSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<RelationshipSearchResult | null>(null);
  const [relationshipTypes, setRelationshipTypes] = useState<DocumentRelationshipType[]>([]);
  const [relationshipTypeId, setRelationshipTypeId] = useState<string>('');
  const [loadingRelationshipTypes, setLoadingRelationshipTypes] = useState(false);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setSearchTerm('');
    setResults([]);
    setSearching(false);
    setSelectedDoc(null);
    setRelationshipTypeId('');
    setDescription('');
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  useEffect(() => {
    if (!isOpen) return;

    setLoadingRelationshipTypes(true);
    documentRelationshipTypeService
      .getAll()
      .then((data: DocumentRelationshipType[]) => {
        const activeTypes = data.filter((type: DocumentRelationshipType) => type.isActive);
        setRelationshipTypes(activeTypes);
        setRelationshipTypeId((current) => current || activeTypes[0]?.id || '');
      })
      .catch(() => {
        setRelationshipTypes([]);
        addToast('Erro ao carregar tipos de relacionamento.', 'error');
      })
      .finally(() => setLoadingRelationshipTypes(false));
  }, [isOpen, addToast]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = searchTerm.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await documentRelationshipService.searchDocuments(documentId, trimmed);
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, documentId]);

  const handleSelectDoc = (doc: RelationshipSearchResult) => {
    setSelectedDoc(doc);
    setRelationshipTypeId((relationshipTypes[0]?.id) || '');
    setDescription('');
  };

  const handleBackToSearch = () => {
    setSelectedDoc(null);
    setRelationshipTypeId((relationshipTypes[0]?.id) || '');
    setDescription('');
  };

  const handleSubmit = async () => {
    if (!selectedDoc) return;
    if (!relationshipTypeId) {
      addToast('Selecione um tipo de relacionamento.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await documentRelationshipService.create({
        sourceDocumentId: documentId,
        targetDocumentId: selectedDoc.id,
        relationshipTypeId,
        description: description.trim() || undefined,
      });
      addToast('Documento vinculado com sucesso.', 'success');
      onRelationshipCreated();
      setSelectedDoc(null);
      setRelationshipTypeId((relationshipTypes[0]?.id) || '');
      setDescription('');
      setSearchTerm('');
      setResults([]);
    } catch {
      addToast('Erro ao vincular documento.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
      initialFocus={searchInputRef}
    >
      <div className="flex min-h-screen items-center justify-center px-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/30" />

        <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="mb-1 text-lg font-medium text-gray-900 flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-blue-600" />
            Relacionar Documento
          </Dialog.Title>
          <p className="mb-4 text-sm text-gray-600">
            Busque e vincule documentos relacionados.
          </p>

          {!selectedDoc ? (
            <>
              {/* Search input */}
              <div className="relative mb-3">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Digite para buscar documentos..."
                  className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Results */}
              <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
                {searching && (
                  <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600" />
                    Buscando...
                  </div>
                )}

                {!searching && searchTerm.trim().length >= 2 && results.length === 0 && (
                  <div className="py-8 text-center text-sm text-gray-400">
                    Nenhum documento encontrado
                  </div>
                )}

                {!searching && searchTerm.trim().length < 2 && (
                  <div className="py-8 text-center text-sm text-gray-400">
                    Digite pelo menos 2 caracteres para buscar
                  </div>
                )}

                {!searching && results.length > 0 && (
                  <ul className="divide-y divide-gray-100">
                    {results.map((doc) => (
                      <li key={doc.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectDoc(doc)}
                          className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-blue-50 transition-colors"
                        >
                          {doc.nodeType === 'folder' ? (
                            <FolderIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <DocumentIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                            <p className="text-xs text-gray-400 truncate">{doc.path}</p>
                          </div>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 flex-shrink-0 mt-0.5">
                            {nodeTypeLabel[doc.nodeType] || doc.nodeType}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Selected document details */}
              <div className="mb-4 rounded-md border border-blue-200 bg-blue-50/50 px-3 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <DocumentIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <p className="text-sm font-medium text-gray-800 truncate">{selectedDoc.title}</p>
                </div>
                <p className="text-xs text-gray-500 ml-6 truncate">{selectedDoc.path}</p>
              </div>

              {/* Relationship type */}
              <div className="mb-4">
                <label className="mb-1.5 block text-xs font-medium tracking-wide text-gray-500">
                  Tipo de relação
                </label>
                {loadingRelationshipTypes ? (
                  <div className="text-sm text-gray-500">Carregando tipos...</div>
                ) : relationshipTypes.length === 0 ? (
                  <div className="text-sm text-gray-500">Nenhum tipo ativo disponível.</div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {relationshipTypes.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setRelationshipTypeId(opt.id)}
                        className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                          relationshipTypeId === opt.id
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-500'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {opt.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Optional description */}
              <div className="mb-4">
                <label className="mb-1.5 block text-xs font-medium tracking-wide text-gray-500">
                  Descrição (opcional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="Descreva a relação entre os documentos..."
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleBackToSearch}
                  disabled={submitting}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={() => void handleSubmit()}
                  disabled={submitting}
                  className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white mr-2" />
                      Vinculando...
                    </>
                  ) : (
                    <>
                      <LinkIcon className="h-4 w-4 mr-1.5" />
                      Vincular
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {/* Close button (always visible) */}
          {!selectedDoc && (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
